import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Radio, Search, RefreshCw, WifiOff, X, Crosshair, Inbox, Gauge, Layers, AlertTriangle, ChevronDown, ChevronUp, UserRound } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import useAuthScope from "../../hooks/useAuthScope";
import { useTheme } from "../../hooks/useTheme";
import useUrlState from "../../hooks/useUrlState";
import useLiveRoutesPolling from "../../hooks/useLiveRoutesPolling";
import useResyncOnVisible from "../../hooks/useResyncOnVisible";
import useDeliveredStore from "../../store/delivered";
import { getOperationTimezone } from "../../utils/timezone";
import { localDateStr } from "../../utils/formatters";
import {
  locationSocketService,
  resolveDriverStatus,
  formatElapsed,
} from "../../services/locationSocketService";
import { AuthService } from "../../services/authservice";
import TrackingMap from "./TrackingMap";
import NextStopsList from "../../components/NextStopsList";
import { STOP_STATUS, statusKey, pkgCoords, driverCounts } from "./clustering";
import { filterPackagesByOperatingDay } from "../liveRoutes/utils/dayBoundary";
import "./liveTracking.scss";

const SNAPSHOT_MS = 6000;

// Colores de estado del chofer leídos de los tokens semánticos (auto dark-mode).
// success = en línea, warning = inactivo, neutral = offline, neutral atenuado = sin GPS.
const STATUS_DOT = {
  online: "rgb(var(--status-success))",
  inactive: "rgb(var(--status-warning))",
  offline: "rgb(var(--status-neutral))",
  "no-gps": "rgb(var(--status-neutral) / 0.5)",
};
const STATUS_TEXT = { online: "En línea", inactive: "Inactivo", offline: "Offline", "no-gps": "Sin GPS" };

// Filtros de la lista del panel por estado del chofer. "all" = sin filtrar;
// "route_no_gps" = pseudo-filtro accionado desde la alerta (en ruta sin GPS).
const STATUS_FILTERS = [
  { id: "all", labelKey: "Todos" },
  { id: "online", labelKey: "En línea" },
  { id: "inactive", labelKey: "Inactivos" },
  { id: "offline", labelKey: "Offline" },
  { id: "no-gps", labelKey: "Sin GPS" },
];

// Capas del mapa agrupadas por TIPO. Cada capa es un toggle independiente;
// la lógica de filtrado consulta `activeLayers[id]` por marcador.
//   · Paquetes: una capa por estado del paquete (claves = statusKey()).
//   · Choferes: una capa por estado del chofer (online/inactive/offline/no-gps).
// Antes había un único toggle "Conductores" booleano y los estados de paquetes
// mezclados a la par — el usuario pidió separarlos por tipo y filtrar choferes
// por estado individual (ej. "Repartidores conectados").
const PACKAGE_LAYERS = [
  { id: "pending",   labelKey: "Pendientes", color: STOP_STATUS.pending.color },
  { id: "route",     labelKey: "En ruta",    color: STOP_STATUS.route.color },
  { id: "delivered", labelKey: "Entregados", color: STOP_STATUS.delivered.color },
  { id: "failed",    labelKey: "Fallidos",   color: STOP_STATUS.failed.color },
  { id: "cancelled", labelKey: "Cancelados", color: STOP_STATUS.cancelled.color },
];

const DRIVER_LAYERS = [
  { id: "driver_online",   labelKey: "Conectados", color: STATUS_DOT.online },
  { id: "driver_inactive", labelKey: "Inactivos",  color: STATUS_DOT.inactive },
  { id: "driver_offline",  labelKey: "Offline",    color: STATUS_DOT.offline },
  { id: "driver_no-gps",   labelKey: "Sin GPS",    color: STATUS_DOT["no-gps"] },
];

const DEFAULT_LAYERS = {
  // paquetes
  route: true,
  delivered: false,
  failed: true,
  pending: false,
  cancelled: false,
  // choferes (sub-toggles por estado real del driver)
  driver_online: true,
  driver_inactive: true,
  driver_offline: false,
  "driver_no-gps": false,
};

export default function LiveTracking() {
  const { t } = useTranslation('tracking');
  const { user } = useAuth();
  const scope = useAuthScope();
  const { isDark } = useTheme();

  const logisticCompanyId = user?.logisticCompanyId || scope.logisticCompanyId;
  const companyId = scope.companyId;
  const isCommerceFleet = user?.company?.hasOwnFleet && !logisticCompanyId;
  const timezone = useMemo(() => getOperationTimezone(user), [user]);
  const date = useMemo(() => localDateStr(new Date(), timezone), [timezone]);

  // Ruta del día por repartidor (polling 15s activo / 60s idle, vía store compartido)
  const { refetch, lastUpdated } = useLiveRoutesPolling({
    date,
    logisticCompanyId: isCommerceFleet ? undefined : logisticCompanyId,
    companyId: isCommerceFleet ? companyId : undefined,
    timezone,
  });
  const delivered = useDeliveredStore((s) => s.delivered[date]);

  // ── Posiciones GPS en vivo (socket + REST) ────────────────────────────
  const positionsRef = useRef(new Map()); // driverId → location
  const onlineRef = useRef(new Set());
  const [snap, setSnap] = useState({ locs: new Map(), online: new Set() });
  const [isConnected, setIsConnected] = useState(false);
  // La carga REST inicial de posiciones terminó (con o sin éxito). Distingue
  // "todavía no llegó el GPS" de "este chofer no tiene GPS".
  const [gpsLoaded, setGpsLoaded] = useState(false);

  const flush = useCallback(() => {
    setSnap({ locs: new Map(positionsRef.current), online: new Set(onlineRef.current) });
  }, []);

  // Foto completa de posiciones por REST: al montar y al volver de un rato con la
  // pestaña oculta (el socket solo trae cambios y puede haberse cortado entretanto).
  const loadPositions = useCallback(async () => {
    try {
      const locs = await locationSocketService.fetchLocationsREST();
      if (Array.isArray(locs)) {
        locs.forEach(({ driverId, location }) => {
          if (!driverId || !location) return;
          positionsRef.current.set(driverId, location);
          if (location.status === "online") onlineRef.current.add(driverId);
        });
        flush();
      }
    } catch {
      /* el panel sigue con datos de ruta aunque falle el GPS */
    }
  }, [flush]);

  useResyncOnVisible(
    useCallback(() => {
      if (!AuthService.hasSession()) return;
      locationSocketService.resume();
      loadPositions();
    }, [loadPositions]),
  );

  useEffect(() => {
    if (!AuthService.hasSession()) {
      // Sin sesión no habrá GPS: se da la carga por terminada para que el deep link
      // pueda caer a la primera parada del repartidor.
      setGpsLoaded(true);
      return;
    }
    const adminId = localStorage.getItem("adminId") || "admin";

    // loadPositions atrapa sus errores: gpsLoaded se marca haya o no GPS.
    loadPositions().then(() => setGpsLoaded(true));

    locationSocketService.connect(adminId);
    const unsubConn = locationSocketService.onConnectionChange(setIsConnected);
    const unsubLoc = locationSocketService.onLocationUpdate(({ driverId, location }) => {
      if (!driverId || !location) return;
      positionsRef.current.set(driverId, location);
      if (location.status === "online") onlineRef.current.add(driverId);
    });
    const unsubStatus = locationSocketService.onDriverStatusChange((driverId, status) => {
      if (status === "online") onlineRef.current.add(driverId);
      else onlineRef.current.delete(driverId);
    });

    const interval = setInterval(() => {
      if (!document.hidden) flush();
    }, SNAPSHOT_MS);

    return () => {
      clearInterval(interval);
      unsubConn();
      unsubLoc();
      unsubStatus();
      locationSocketService.disconnect();
    };
  }, [flush, loadPositions]);

  // ── UI state ──────────────────────────────────────────────────────────
  // Filtros + driver seleccionado persistidos en la URL (deep-link compartible).
  // Defaults = los actuales ⇒ sin params el comportamiento es idéntico al previo.
  const [selectedDriverId, setSelectedDriverId] = useUrlState("driver", null);
  const [searchQuery, setSearchQuery] = useUrlState("q", "");
  const [statusFilter, setStatusFilter] = useUrlState("status", "all");
  // Las capas del mapa y su panel quedan locales (preferencia efímera del mapa).
  const [activeLayers, setActiveLayers] = useState(DEFAULT_LAYERS);
  const [layersOpen, setLayersOpen] = useState(false);
  const [focusTarget, setFocusTarget] = useState(null);
  // Hoja inferior (mobile): colapsada deja visible solo handle + header
  const [sheetCollapsed, setSheetCollapsed] = useState(false);
  const layersRef = useRef(null);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Cierra el panel de capas al cambiar de ruta, presionar Esc o click fuera.
  useEffect(() => { setLayersOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!layersOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setLayersOpen(false); };
    const onClick = (e) => {
      if (layersRef.current && !layersRef.current.contains(e.target)) setLayersOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [layersOpen]);

  const toggleLayer = useCallback(
    (id) => setActiveLayers((s) => ({ ...s, [id]: !s[id] })),
    []
  );
  const setAllLayers = useCallback(
    (value) => setActiveLayers((s) => Object.fromEntries(Object.keys(s).map((k) => [k, value]))),
    []
  );
  const activeLayerCount = useMemo(
    () => Object.values(activeLayers).filter(Boolean).length,
    [activeLayers]
  );

  const driverStatus = useCallback(
    (id) => {
      const loc = snap.locs.get(id);
      if (!loc) return "no-gps";
      return resolveDriverStatus({ ...loc, driverId: id }, snap.online);
    },
    [snap]
  );

  // ── Datos derivados ──────────────────────────────────────────────────
  const drivers = useMemo(
    () =>
      (delivered || []).map((d) => {
        // Vista "en vivo" = sólo las gestiones del día operativo. El back arrastra
        // en la ruta del repartidor entregas/fallidos de días anteriores; los
        // filtramos por la fecha de su último cambio de estado (borde de día a las
        // 2am, mismo criterio que liveRoutes). Los "en ruta" se mantienen siempre.
        const packages = filterPackagesByOperatingDay(d.packages || [], date, timezone);
        return {
          driverId: d._id,
          name: d.delivererFullName || t('Repartidor'),
          packages,
          // Conteos recomputados desde los paquetes ya filtrados al día (no usamos
          // los agregados del back, que pueden venir acumulados con días previos).
          counts: driverCounts({ packages }),
        };
      }),
    [delivered, date, timezone, t]
  );

  const driverMarkers = useMemo(
    () =>
      drivers
        .filter((d) => snap.locs.has(d.driverId))
        .map((d) => ({
          driverId: d.driverId,
          name: d.name,
          location: snap.locs.get(d.driverId),
          status: driverStatus(d.driverId),
        }))
        // Filtrar por capa de driver (online/inactive/offline/no-gps).
        // Si el status no calza con ninguna capa conocida, lo mostramos por
        // defecto para no esconder data legítima por un edge case del back.
        .filter((d) => {
          const layerKey = `driver_${d.status}`;
          return layerKey in activeLayers ? !!activeLayers[layerKey] : true;
        }),
    [drivers, snap, driverStatus, activeLayers]
  );

  const stops = useMemo(() => {
    const src = selectedDriverId ? drivers.filter((d) => d.driverId === selectedDriverId) : drivers;
    const out = [];
    for (const d of src) {
      for (let i = 0; i < d.packages.length; i++) {
        const p = d.packages[i];
        const key = statusKey(p.status);
        if (!activeLayers[key]) continue;
        const c = pkgCoords(p);
        if (!c) continue;
        out.push({
          id: p._id,
          // Key de render: un mismo paquete puede venir en la ruta de 2
          // repartidores y el back a veces omite _id, así que el _id solo
          // no es único; se compone con el driver y la posición en su ruta.
          uid: `${d.driverId}:${p._id || 'p'}:${i}`,
          lat: c[0],
          lng: c[1],
          key,
          followingNumber: p.followingNumber,
          driverName: d.name,
          address: p.address_line || p.shipping_address?.municipality?.name || null,
        });
      }
    }
    return out;
  }, [drivers, selectedDriverId, activeLayers]);

  const stats = useMemo(() => {
    const s = { assigned: 0, route: 0, delivered: 0, failed: 0, pending: 0, cancelled: 0, activeDrivers: 0 };
    for (const d of drivers) {
      const c = d.counts;
      s.assigned += c.assigned;
      s.route += c.route;
      s.delivered += c.delivered;
      s.failed += c.failed;
      s.pending += c.pending;
      s.cancelled += c.cancelled;
      if (c.assigned > 0) s.activeDrivers++;
    }
    s.compliance = s.delivered + s.failed > 0 ? Math.round((s.delivered / (s.delivered + s.failed)) * 100) : 0;
    return s;
  }, [drivers]);

  // Conteos de chofer por estado (alimenta los números de las capas de Choferes).
  const driverStats = useMemo(() => {
    const s = { driver_online: 0, driver_inactive: 0, driver_offline: 0, "driver_no-gps": 0 };
    for (const d of drivers) {
      const st = driverStatus(d.driverId);
      const key = `driver_${st}`;
      if (key in s) s[key] += 1;
    }
    return s;
  }, [drivers, driverStatus]);

  // "En ruta sin GPS": repartidores con paquetes en ruta pero sin posición GPS
  // viva — el cruce que más urge en una sala de control (no podés ubicarlos).
  // Se calcula sobre TODOS los choferes, independiente del filtro activo.
  const enRutaSinGps = useMemo(
    () => drivers.filter((d) => d.counts.route > 0 && driverStatus(d.driverId) === "no-gps").length,
    [drivers, driverStatus]
  );

  // Conteos por estado del chofer para los pills del filtro (incluye "all").
  const statusCounts = useMemo(
    () => ({
      all: drivers.length,
      online: driverStats.driver_online,
      inactive: driverStats.driver_inactive,
      offline: driverStats.driver_offline,
      "no-gps": driverStats["driver_no-gps"],
    }),
    [drivers.length, driverStats]
  );

  const driverList = useMemo(() => {
    let list = drivers.map((d) => ({ ...d, status: driverStatus(d.driverId), location: snap.locs.get(d.driverId) }));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((d) => d.name.toLowerCase().includes(q));
    }
    // Filtro por estado del chofer. "route_no_gps" es el accionado por la alerta.
    if (statusFilter === "route_no_gps") {
      list = list.filter((d) => d.status === "no-gps" && d.counts.route > 0);
    } else if (statusFilter !== "all") {
      list = list.filter((d) => d.status === statusFilter);
    }
    return list.sort((a, b) => b.counts.route - a.counts.route || a.name.localeCompare(b.name));
  }, [drivers, snap, searchQuery, driverStatus, statusFilter]);

  const selected = useMemo(
    () => driverList.find((d) => d.driverId === selectedDriverId) || null,
    [driverList, selectedDriverId]
  );

  // ── Handlers ──────────────────────────────────────────────────────────
  const focusOnDriver = useCallback(
    (id) => {
      const pos = positionsRef.current.get(id);
      if (pos) {
        setFocusTarget({ lat: pos.latitude, lng: pos.longitude, zoom: 14, token: Date.now() });
        return;
      }
      // Sin GPS: encuadrar en su primera parada geolocalizable del día
      const d = drivers.find((x) => x.driverId === id);
      const stop = d?.packages.map(pkgCoords).find(Boolean);
      if (stop) setFocusTarget({ lat: stop[0], lng: stop[1], zoom: 13, token: Date.now() });
    },
    [drivers]
  );

  // Deep link `?driver=<id>`: encuadra al chofer de la URL una sola vez al cargar.
  // `focusOnDriver` solo corría desde clics, así que el mapa quedaba en la vista
  // por defecto. Se prefiere la posición GPS (puede llegar tarde, por REST o
  // socket); la parada de respaldo solo se usa cuando la carga de posiciones ya
  // terminó y el chofer no tiene GPS. Si el usuario cambia o quita la selección
  // antes de que se pueda enfocar, se abandona para no pelearle el mapa.
  const initialDriverRef = useRef(selectedDriverId);
  const initialFocusDoneRef = useRef(!selectedDriverId);
  useEffect(() => {
    if (initialFocusDoneRef.current) return;
    const id = initialDriverRef.current;
    if (selectedDriverId !== id) {
      initialFocusDoneRef.current = true;
      return;
    }
    const hasGps = positionsRef.current.has(id);
    const hasStop = gpsLoaded && drivers.some((d) => d.driverId === id && d.packages.some(pkgCoords));
    if (hasGps || hasStop) {
      initialFocusDoneRef.current = true;
      focusOnDriver(id);
    }
  }, [snap, drivers, gpsLoaded, selectedDriverId, focusOnDriver]);

  const handleSelect = useCallback(
    (id) => {
      // Toggle: si ya estaba seleccionado lo deselecciona; si no, lo selecciona
      // y encuadra. Lee el valor vigente (derivado de la URL) sin updater fn para
      // no disparar el efecto de foco dentro de la actualización del querystring.
      if (selectedDriverId === id) {
        setSelectedDriverId(null);
      } else {
        setSelectedDriverId(id);
        focusOnDriver(id);
      }
    },
    [selectedDriverId, setSelectedDriverId, focusOnDriver]
  );

  const loading = delivered === undefined;

  return (
    <div className={`lt ${sheetCollapsed ? "lt--sheet-collapsed" : ""}`}>
      <TrackingMap
        isDark={isDark}
        stops={stops}
        drivers={driverMarkers}
        showDrivers={true /* filtrado se hace en driverMarkers por capa de status */}
        selectedDriverId={selectedDriverId}
        onSelectDriver={handleSelect}
        focusTarget={focusTarget}
      />

      {/* ── Tira de stats + control de capas (sustituye al chip row) ── */}
      <div className="lt-stats">
        <div className="lt-stats__summary">
          <span className="lt-stats__brand"><Radio size={14} />{t('Seguimiento en vivo')}</span>
          <span className="lt-stats__metric"><b>{stats.activeDrivers}</b>{t('repartidores')}</span>
          <span className="lt-stats__metric"><b>{stats.assigned}</b>{t('pedidos hoy')}</span>
          <span className="lt-stats__metric lt-stats__metric--accent"><Gauge size={13} /><b>{stats.compliance}%</b>{t('cumplimiento')}</span>
        </div>
        <div className="lt-stats__filters" ref={layersRef}>
          <button
            type="button"
            className={`lt-layers__btn ${layersOpen ? "lt-layers__btn--open" : ""}`}
            onClick={() => setLayersOpen((o) => !o)}
            aria-expanded={layersOpen}
            aria-haspopup="true"
          >
            <Layers size={14} />
            <span>{t('Capas')}</span>
            <span className="lt-layers__count">{activeLayerCount}</span>
          </button>
          {layersOpen && (
            <div className="lt-layers__panel" role="dialog" aria-label={t('Capas')}>
              <section className="lt-layers__group">
                <h3 className="lt-layers__grouphead">{t('Paquetes')}</h3>
                <ul className="lt-layers__list">
                  {PACKAGE_LAYERS.map((l) => {
                    const label = t(l.labelKey);
                    return (
                      <li key={l.id}>
                        <label className="lt-layers__row">
                          <input
                            type="checkbox"
                            checked={!!activeLayers[l.id]}
                            onChange={() => toggleLayer(l.id)}
                            aria-label={t('Toggle {{label}}', { label })}
                            className="lt-layers__check"
                          />
                          <span className="lt-layers__dot" style={{ background: l.color }} />
                          <span className="lt-layers__label">{label}</span>
                          <b className="lt-layers__num">{stats[l.id] ?? 0}</b>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>

              <section className="lt-layers__group">
                <h3 className="lt-layers__grouphead">{t('Choferes')}</h3>
                <ul className="lt-layers__list">
                  {DRIVER_LAYERS.map((l) => {
                    const label = t(l.labelKey);
                    return (
                      <li key={l.id}>
                        <label className="lt-layers__row">
                          <input
                            type="checkbox"
                            checked={!!activeLayers[l.id]}
                            onChange={() => toggleLayer(l.id)}
                            aria-label={t('Toggle {{label}}', { label })}
                            className="lt-layers__check"
                          />
                          <span className="lt-layers__dot" style={{ background: l.color }} />
                          <span className="lt-layers__label">{label}</span>
                          <b className="lt-layers__num">{driverStats[l.id] ?? 0}</b>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>

              <div className="lt-layers__actions">
                <button type="button" className="lt-layers__action" onClick={() => setAllLayers(true)}>
                  {t('Mostrar todo')}
                </button>
                <button type="button" className="lt-layers__action" onClick={() => setAllLayers(false)}>
                  {t('Ocultar todo')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Panel derecho ── */}
      <aside className={`lt-panel ${sheetCollapsed ? "lt-panel--collapsed" : ""}`}>
        <button
          type="button"
          className="lt-panel__handle"
          onClick={() => setSheetCollapsed((v) => !v)}
          aria-expanded={!sheetCollapsed}
          aria-label={sheetCollapsed ? t('Expandir panel') : t('Colapsar panel')}
        >
          {sheetCollapsed ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        <header className="lt-panel__head">
          <div>
            <h1 className="lt-panel__title">{t('Repartidores')}</h1>
            <p className={`lt-panel__conn ${isConnected ? "lt-panel__conn--live" : ""}`}>
              {isConnected ? (
                <><span className="lt-panel__pulse" />{t('Tiempo real')}</>
              ) : (
                <><WifiOff size={11} />{t('Sin conexión en vivo')}</>
              )}
              {lastUpdated && <span className="lt-panel__updated">· {formatElapsed(lastUpdated)}</span>}
            </p>
          </div>
          <button className="lt-panel__refresh" onClick={refetch} title={t('Actualizar ahora')} aria-label={t('Actualizar')}>
            <RefreshCw size={15} />
          </button>
        </header>

        <div className="lt-panel__search">
          <Search size={14} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('Buscar repartidor…')}
          />
        </div>

        {/* Filtro por estado del chofer — pills compactos con conteo en vivo. */}
        <div className="lt-panel__statusbar" role="tablist" aria-label={t('Filtrar por estado del chofer')}>
          {STATUS_FILTERS.map((f) => {
            const active = statusFilter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`lt-statuspill ${active ? "lt-statuspill--active" : ""}`}
                onClick={() => setStatusFilter(f.id)}
              >
                {f.id !== "all" && <span className="lt-statuspill__dot" style={{ background: STATUS_DOT[f.id] }} />}
                <span>{t(f.labelKey)}</span>
                <b>{statusCounts[f.id] ?? 0}</b>
              </button>
            );
          })}
        </div>

        {/* Alerta operativa: en ruta sin GPS. Click → filtra la lista a ese cruce. */}
        {enRutaSinGps > 0 && (
          <button
            type="button"
            className={`lt-alert ${statusFilter === "route_no_gps" ? "lt-alert--active" : ""}`}
            onClick={() => setStatusFilter((p) => (p === "route_no_gps" ? "all" : "route_no_gps"))}
          >
            <AlertTriangle size={14} />
            <span className="lt-alert__text">
              <b>{enRutaSinGps}</b> {t('en ruta sin GPS')}
            </span>
            <span className="lt-alert__hint">{statusFilter === "route_no_gps" ? t('Quitar') : t('Ver')}</span>
          </button>
        )}

        {selectedDriverId && <NextStopsList driverId={selectedDriverId} />}

        <div className="lt-panel__list">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <div key={i} className="lt-row lt-row--skeleton" />)
          ) : driverList.length === 0 ? (
            <div className="lt-empty">
              <Inbox size={30} />
              <p className="lt-empty__title">
                {searchQuery.trim() || statusFilter !== "all" ? t('Sin resultados') : t('Sin repartidores con ruta hoy')}
              </p>
              {!searchQuery.trim() && statusFilter === "all" && (
                <p className="lt-empty__desc">{t('Cuando se asignen pedidos aparecerán aquí.')}</p>
              )}
            </div>
          ) : (
            driverList.map((d) => (
              <button
                key={d.driverId}
                className={`lt-row ${selectedDriverId === d.driverId ? "lt-row--active" : ""}`}
                onClick={() => handleSelect(d.driverId)}
              >
                <span className="lt-row__avatar" style={{ background: STATUS_DOT[d.status] }}>
                  {d.name.substring(0, 2).toUpperCase()}
                  <span className="lt-row__avatar-dot" style={{ background: STATUS_DOT[d.status] }} />
                </span>
                <span className="lt-row__body">
                  <span className="lt-row__name">{d.name}</span>
                  <span className="lt-row__counts">
                    <span style={{ color: STOP_STATUS.route.color }}>{d.counts.route} {t('en ruta')}</span>
                    <span style={{ color: STOP_STATUS.delivered.color }}>{d.counts.delivered} {t('entreg.')}</span>
                    {d.counts.failed > 0 && <span style={{ color: STOP_STATUS.failed.color }}>{d.counts.failed} {t('fall.')}</span>}
                  </span>
                </span>
                {d.status === "no-gps" && d.counts.route > 0 && (
                  <span className="lt-row__alert" title={t('En ruta sin señal GPS')} aria-label={t('En ruta sin señal GPS')}>
                    <AlertTriangle size={13} />
                  </span>
                )}
                <span className="lt-row__compliance">{d.counts.compliance}%</span>
              </button>
            ))
          )}
        </div>

        {selected && (
          <div className="lt-detail">
            <div className="lt-detail__head">
              <span className="lt-detail__name">
                <span className="lt-detail__dot" style={{ background: STATUS_DOT[selected.status] }} />
                {selected.name}
              </span>
              <button className="lt-detail__close" onClick={() => setSelectedDriverId(null)} aria-label={t('Quitar selección')}>
                <X size={15} />
              </button>
            </div>

            <p className="lt-detail__gps">
              {t(STATUS_TEXT[selected.status])}
              {selected.location?.speed != null && ` · ${(selected.location.speed * 3.6).toFixed(0)} km/h`}
              {selected.location?.timestamp && ` · ${formatElapsed(selected.location.timestamp)}`}
            </p>

            <div className="lt-detail__counts">
              <div className="lt-detail__count"><b>{selected.counts.assigned}</b><span>{t('Asignados')}</span></div>
              <div className="lt-detail__count" style={{ color: STOP_STATUS.route.color }}><b>{selected.counts.route}</b><span>{t('En ruta')}</span></div>
              <div className="lt-detail__count" style={{ color: STOP_STATUS.delivered.color }}><b>{selected.counts.delivered}</b><span>{t('Entregados')}</span></div>
              <div className="lt-detail__count" style={{ color: STOP_STATUS.failed.color }}><b>{selected.counts.failed}</b><span>{t('Fallidos')}</span></div>
              <div className="lt-detail__count" style={{ color: STOP_STATUS.cancelled.color }}><b>{selected.counts.cancelled}</b><span>{t('Cancelados')}</span></div>
            </div>

            <div className="lt-detail__bar">
              <div className="lt-detail__bar-head">
                <span>{t('Cumplimiento')}</span>
                <b>{selected.counts.compliance}%</b>
              </div>
              <div className="lt-detail__bar-track">
                <div
                  className="lt-detail__bar-fill"
                  style={{
                    width: `${Math.min(100, selected.counts.compliance)}%`,
                    background:
                      selected.counts.compliance >= 80
                        ? STOP_STATUS.delivered.color
                        : selected.counts.compliance >= 50
                        ? STOP_STATUS.route.color
                        : STOP_STATUS.failed.color,
                  }}
                />
              </div>
            </div>

            <div className="lt-detail__actions">
              <button className="lt-detail__center" onClick={() => focusOnDriver(selected.driverId)}>
                <Crosshair size={14} />
                {t('Centrar en el mapa')}
              </button>
              {/* En Clarity se veía al admin salir a Usuarios y buscar por nombre al mismo
                  repartidor que tenía seleccionado acá. Solo bajo /admin: la vista también
                  la usa el comercio (/home), que no tiene acceso a la ficha. */}
              {isAdminRoute && (
                <Link className="lt-detail__profile" to={`/admin/users?user=${encodeURIComponent(selected.driverId)}`}>
                  <UserRound size={14} />
                  {t('Ver ficha')}
                </Link>
              )}
            </div>
          </div>
        )}
      </aside>

      {!isConnected && !loading && (
        <div className="lt-banner">
          <WifiOff size={14} />
          {t('Sin conexión en vivo, mostrando la última posición conocida.')}
        </div>
      )}
    </div>
  );
}
